import React, { useEffect, useRef, useState } from 'react';
import { pushService } from '../services/pushService';

/**
 * QueueBoard: full-screen wallboard for clinic displays.
 *
 * Shows the live queue count in giant digits, flashes and chimes when a
 * student joins, and needs no login: it only reads the push hub's public
 * /count endpoint (no names, no Airtable access). Open /board on any
 * clinic PC, TV browser, or spare tablet and leave it full screen.
 */

const POLL_MS = 5000;

const QueueBoard = () => {
  const [state, setState] = useState(null); // {count, emergencies, sessionActive, updatedAt}
  const [started, setStarted] = useState(false);
  const [flash, setFlash] = useState(false);
  const [offline, setOffline] = useState(false);
  const previousCountRef = useRef(null);
  const audioContextRef = useRef(null);
  const failuresRef = useRef(0);

  const hubUrl = pushService.getHubUrl();

  useEffect(() => {
    if (!started || !hubUrl) return;

    let cancelled = false;

    const chime = (isEmergency) => {
      const ctx = audioContextRef.current;
      if (!ctx) return;
      try {
        // Two-tone "ding-dong"; three urgent tones for emergencies.
        const tones = isEmergency ? [880, 880, 880] : [660, 880];
        tones.forEach((freq, i) => {
          const oscillator = ctx.createOscillator();
          const gain = ctx.createGain();
          oscillator.connect(gain);
          gain.connect(ctx.destination);
          oscillator.type = 'sine';
          oscillator.frequency.value = freq;
          const start = ctx.currentTime + i * 0.35;
          gain.gain.setValueAtTime(0, start);
          gain.gain.linearRampToValueAtTime(0.5, start + 0.05);
          gain.gain.exponentialRampToValueAtTime(0.01, start + 0.3);
          oscillator.start(start);
          oscillator.stop(start + 0.32);
        });
      } catch (error) {
        console.warn('Board chime failed:', error);
      }
    };

    const poll = async () => {
      try {
        const response = await fetch(`${hubUrl}/count`, { cache: 'no-store' });
        if (!response.ok) throw new Error(`status ${response.status}`);
        const next = await response.json();
        if (cancelled) return;

        failuresRef.current = 0;
        setOffline(false);

        const previous = previousCountRef.current;
        if (previous !== null && next.count > previous) {
          setFlash(true);
          chime(next.emergencies > 0);
          setTimeout(() => setFlash(false), 1200);
        }
        previousCountRef.current = next.count;
        setState(next);
      } catch (error) {
        failuresRef.current += 1;
        if (failuresRef.current >= 3 && !cancelled) {
          setOffline(true);
        }
      }
    };

    poll();
    const interval = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [started, hubUrl]);

  const startBoard = () => {
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    } catch (error) {
      console.warn('Audio not available for board:', error);
    }
    setStarted(true);
  };

  const hasEmergency = state && state.emergencies > 0;
  const sessionActive = state && state.sessionActive;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: flash ? (hasEmergency ? '#7a1010' : '#0e4f4a') : '#101418',
        transition: 'background-color 0.25s ease',
        color: 'white',
        fontFamily: 'inherit',
        cursor: started ? 'default' : 'pointer',
        userSelect: 'none'
      }}
      onClick={started ? undefined : startBoard}
    >
      {!hubUrl && (
        <p style={{ fontSize: '24px', maxWidth: '600px', textAlign: 'center' }}>
          The notification hub is not configured. Set REACT_APP_PUSH_HUB_URL and
          rebuild (see worker/README.md).
        </p>
      )}

      {hubUrl && !started && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🦷</div>
          <h1 style={{ fontSize: '42px', margin: '0 0 12px 0' }}>Queue Board</h1>
          <p style={{ fontSize: '22px', opacity: 0.8 }}>Tap anywhere to start (enables sound)</p>
        </div>
      )}

      {hubUrl && started && (
        <>
          {hasEmergency && (
            <div
              style={{
                position: 'absolute',
                top: '4vh',
                padding: '12px 32px',
                backgroundColor: '#c62828',
                borderRadius: '12px',
                fontSize: '4vmin',
                fontWeight: 'bold',
                letterSpacing: '2px',
                animation: 'boardPulse 1s ease-in-out infinite'
              }}
            >
              🚨 EMERGENCY IN QUEUE
            </div>
          )}

          <div
            style={{
              fontSize: '40vmin',
              fontWeight: 800,
              lineHeight: 1,
              color: !sessionActive ? '#3a4450' : hasEmergency ? '#ff5252' : '#4ecdc4',
              textShadow: sessionActive ? '0 0 6vmin rgba(78, 205, 196, 0.35)' : 'none'
            }}
          >
            {state ? state.count : '–'}
          </div>

          <div style={{ fontSize: '6vmin', letterSpacing: '8px', opacity: 0.85 }}>
            {sessionActive ? 'WAITING' : 'NO ACTIVE SESSION'}
          </div>

          <div
            style={{
              position: 'absolute',
              bottom: '3vh',
              fontSize: '2.2vmin',
              opacity: 0.5,
              display: 'flex',
              gap: '24px',
              alignItems: 'center'
            }}
          >
            <span>notesncodes.com</span>
            {offline && (
              <span style={{ color: '#ffab40', fontWeight: 'bold' }}>⚠ CONNECTION LOST</span>
            )}
          </div>

          <style>{`
            @keyframes boardPulse {
              0%, 100% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.04); opacity: 0.85; }
            }
          `}</style>
        </>
      )}
    </div>
  );
};

export default QueueBoard;
